// The hex viewer's refresh by pull (Task 13, R37): the calibration is read once at the open, and again, once, after the writes that changed bytes
// settle, and only while the viewer can be seen. This replaces the live updates the editor once had; the only news the editor wants is its own writes.
// A plain module with no React in it: the provider gives it the read (`fetchCalibration`), where the data goes (`onData`) and whether the viewer is
// visible (`isVisible`), and a test gives it timers of its own.

/**
 * @param {Object} options
 * @param {function(): Promise<{data: string, dataModified: ?string}>} options.fetchCalibration - one read of the calibration
 * @param {function({data: string, dataModified: ?string}): void} options.onData - gets what a read answered
 * @param {function(): boolean} options.isVisible - whether the hex viewer is shown now
 * @param {number} [options.debounceMs] - how long after the last byte-changing write the read is made
 * @param {function} [options.setTimer] - setTimeout
 * @param {function} [options.clearTimer] - clearTimeout
 * @returns {{noteWrite: function((number|boolean)): void, noteVisibility: function(boolean): void, dispose: function(): void}}
 *   noteWrite(changed) - a write finished: `changed` is the number of bytes it changed (0 does nothing), or `true` when a write changed bytes that
 *     are not counted (an axis save, an undo, a reset step, or a request whose answer was lost and then read). While the viewer is visible it arms one
 *     timer, re-armed by every further write, whose expiry reads once. While the viewer is hidden it only remembers that the viewer is stale.
 *   noteVisibility(visible) - the viewer was shown or hidden. Shown with news waiting, it reads at once, with no delay. Hidden, it drops a timer that
 *     is waiting, because nothing is read while the viewer cannot be seen, and remembers that the viewer is stale.
 *   dispose() - drops the timer, and the refresher does nothing after.
 */
export const createHexRefresher = ({fetchCalibration, onData, isVisible, debounceMs = 2000, setTimer = setTimeout, clearTimer = clearTimeout}) => {
    let timer = null;
    let stale = false;
    let disposed = false;
    // The newest read started: an answer that is not the newest read's is dropped, whichever lands first. (A newer read that fails after an older one landed
    // leaves the older answer thrown away; the viewer keeps what it has until the next write, or a hide and show.)
    let newest = 0;

    const disarm = () => {
        if (timer !== null) {
            clearTimer(timer);
            timer = null;
        }
    };

    // One read. The news is spent as the read starts; a read that fails puts it back, and says so on the console, so that the next time the viewer is
    // shown (or the next write) the read is made again.
    const fetchNow = async () => {
        stale = false;
        newest += 1;
        const mine = newest;
        try {
            const result = await fetchCalibration();
            if (!disposed && mine === newest) {
                onData(result);
            }
        } catch (error) {
            console.error(error);
            if (mine === newest) {
                stale = true;
            }
        }
    };

    const noteWrite = changed => {
        if (disposed || !(changed === true || changed > 0)) {
            return;
        }
        if (!isVisible()) {
            stale = true;
            return;
        }
        disarm();
        timer = setTimer(() => {
            timer = null;
            fetchNow();
        }, debounceMs);
    };

    const noteVisibility = visible => {
        if (disposed) {
            return;
        }
        if (!visible) {
            if (timer !== null) {
                disarm();
                stale = true;
            }
            return;
        }
        if (stale) {
            // A write that is waiting out its delay is answered by this read.
            disarm();
            fetchNow();
        }
    };

    const dispose = () => {
        disposed = true;
        disarm();
    };

    return {noteWrite, noteVisibility, dispose};
};
